/**
 * ProfessionalMultiSelect
 * Feature: 005-clinic-scheduling
 *
 * Multi-select dropdown with search for selecting professionals.
 * Shows selected names as badges inside the trigger.
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { Check, ChevronDown, X, Search } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export interface ProfessionalOption {
  id: string
  full_name: string
}

interface ProfessionalMultiSelectProps {
  professionals: ProfessionalOption[]
  value: string[]
  onChange: (ids: string[]) => void
  placeholder?: string
  disabled?: boolean
}

export function ProfessionalMultiSelect({
  professionals,
  value,
  onChange,
  placeholder = 'Selecione os profissionais...',
  disabled = false,
}: ProfessionalMultiSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const selected = professionals.filter((p) => value.includes(p.id))
  const filtered = professionals.filter((p) =>
    p.full_name.toLowerCase().includes(search.toLowerCase())
  )

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Focus search when opening
  useEffect(() => {
    if (open) {
      setTimeout(() => searchRef.current?.focus(), 50)
    }
  }, [open])

  const toggle = useCallback(
    (id: string) => {
      if (value.includes(id)) {
        onChange(value.filter((v) => v !== id))
      } else {
        onChange([...value, id])
      }
    },
    [value, onChange]
  )

  const remove = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation()
      onChange(value.filter((v) => v !== id))
    },
    [value, onChange]
  )

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <div
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => !disabled && setOpen((o) => !o)}
        className={cn(
          'flex min-h-9 w-full cursor-pointer flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-3 py-1.5 text-sm shadow-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          open && 'ring-1 ring-ring',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        {selected.length === 0 ? (
          <span className="text-muted-foreground flex-1">{placeholder}</span>
        ) : (
          <>
            {selected.map((p) => (
              <Badge
                key={p.id}
                variant="secondary"
                className="flex items-center gap-1 pr-1 text-xs"
              >
                {p.full_name}
                <button
                  type="button"
                  onClick={(e) => remove(e, p.id)}
                  className="rounded-full hover:bg-muted-foreground/20 p-0.5 transition-colors"
                  tabIndex={-1}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </>
        )}
        <ChevronDown
          className={cn(
            'ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-[9999] mt-1 w-full rounded-md border bg-popover shadow-md">
          {/* Search */}
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar profissional..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Options */}
          <div
            role="listbox"
            className="max-h-52 overflow-y-auto py-1"
          >
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">
                Nenhum profissional encontrado.
              </p>
            ) : (
              filtered.map((prof) => {
                const isSelected = value.includes(prof.id)
                return (
                  <div
                    key={prof.id}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => toggle(prof.id)}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground',
                      isSelected && 'bg-accent/50'
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        isSelected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input'
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </div>
                    <span>{prof.full_name}</span>
                  </div>
                )
              })
            )}
          </div>

          {/* Footer count */}
          {selected.length > 0 && (
            <div className="border-t px-3 py-1.5 text-xs text-muted-foreground">
              {selected.length} selecionado{selected.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
